import React, { useState, createContext, useCallback } from 'react';
import {
  SimDatesResponse,
  SimDateData,
  CommentData,
  ArtDatesResponse,
  ArtDateData
} from '../models/SimArtAdminInterface';
import { API } from 'aws-amplify';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

const SimArtAdminContext = createContext({
  simDatesData: [] as SimDateData[],
  artDatesData: [] as ArtDateData[],
  getSimDatesData: () =>
    Promise.resolve({ simDatesList: [] } as SimDatesResponse),
  getArtDatesData: () =>
    Promise.resolve({ artDatesList: [] } as ArtDatesResponse),
  updateSimDate: (simDateId: number, updatedData: Partial<SimDateData>) =>
    Promise.resolve(true),
  updateArtDate: (artDateId: number, updatedData: any) => Promise.resolve(true),
  addComment: (simDateId: number, comment: Omit<CommentData, 'id'>) =>
    Promise.resolve(true)
});

export const SimArtAdminProvider = (props) => {
  // Extend dayjs with UTC and timezone plugins
  dayjs.extend(utc);
  dayjs.extend(timezone);

  const [simDatesData, setSimDatesData] = useState<SimDateData[]>([]);
  const [artDatesData, setArtDatesData] = useState<ArtDateData[]>([]);

  const getArtDatesData = useCallback(async (): Promise<ArtDatesResponse> => {
    try {
      const response = await API.get('crewlife', '/art/settings/retrieve', {});

      const artDatesDataMapped: ArtDateData[] = (
        response.artRequests || []
      ).map((req: any) => ({
        id: req.artRequestId,
        year: req.artYear,
        month: getMonthName(req.artMonth),
        daterange:
          formatDateRange(req.artOpenDateTime, req.artCloseDateTime) || '',
        submissionClose: req.artCloseDateTime
          ? formatTime(req.artCloseDateTime)
          : '',
        sendDates: req.artSendDate ? formatDate(req.artSendDate) : '',
        status: req.artStatus || 'NOT READY',
        comments: req.comments ? req.comments.length : 0,
        commentsData: req.comments
          ? req.comments.map((c: any) => ({
              id: c.commentId?.toString() ?? '',
              author: c.commentCreatedBy ?? '',
              timestamp: c.createdDate ? formatDate(c.createdDate) : '',
              content: c.comment ?? ''
            }))
          : [],
        lastmodified: req.modifiedDate
          ? `Modified on ${formatDate(req.modifiedDate)}`
          : req.createdDate
            ? `Created on ${formatDate(req.createdDate)}`
            : '',
        edit: 'Edit',
        artOpenDateTime: req.artOpenDateTime,
        artCloseDate: req.artCloseDateTime,
        artCloseDateTime: req.artCloseDateTime,
        artSendDate: req.artSendDate
      }));

      setArtDatesData(artDatesDataMapped);

      return {
        artDatesList: artDatesDataMapped
      };
    } catch (e: any) {
      setArtDatesData([]);

      let errorMessage =
        'An unexpected error occurred while fetching ART data.';

      if (e.response?.data?.details) {
        errorMessage = e.response.data.details;
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.message) {
        errorMessage = e.message;
      }
      const customError = new Error(errorMessage);
      (customError as any).response = e.response;
      throw customError;
    }
  }, []);

  const getSimDatesData = useCallback(async (): Promise<SimDatesResponse> => {
    try {
      const response = await API.get('crewlife', '/sim/settings/retrieve', {});

      const simDatesDataMapped: SimDateData[] = (
        response.simRequests || []
      ).map((req: any) => ({
        id: req.simRequestId,
        simYear: req.simYear,
        simMonth: getMonthName(req.simMonth),
        daterange:
          formatDateRange(req.simOpenDateTime, req.simCloseDateTime) || '',
        simOpenDateTime: req.simOpenDateTime,
        simCloseDate: req.simCloseDateTime,
        simCloseDateTime: req.simCloseDateTime
          ? formatTime(req.simCloseDateTime)
          : '',
        simSendDate: req.simSendDate ? formatDate(req.simSendDate) : '',
        // Store raw API values for exact resubmission
        rawSimCloseDateTime: req.simCloseDateTime,
        rawSimSendDate: req.simSendDate,
        simStatus: req.simStatus || 'NOT READY',
        commentsLength: req.comments ? req.comments.length : 0,
        comments: req.comments
          ? req.comments.map((c: any) => ({
              id: c.commentId?.toString() ?? '',
              author: c.commentCreatedBy ?? '',
              timestamp: c.createdDate ? formatDate(c.createdDate) : '',
              content: c.comment ?? ''
            }))
          : [],
        modifiedDate: req.modifiedDate
          ? `Modified on ${formatDate(req.modifiedDate)}`
          : req.createdDate
            ? `Created on ${formatDate(req.createdDate)}`
            : '',
        edit: 'Edit'
      }));

      setSimDatesData(simDatesDataMapped);

      return {
        simDatesList: simDatesDataMapped
      };
    } catch (e: any) {
      setSimDatesData([]);

      let errorMessage =
        'An unexpected error occurred while fetching SIM data.';

      if (e.response?.data?.details) {
        errorMessage = e.response.data.details;
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.response?.data?.error) {
        errorMessage = e.response.data.error;
      } else if (e.message) {
        errorMessage = e.message;
      }
      const customError = new Error(errorMessage);
      (customError as any).response = e.response;
      throw customError;
    }
  }, []);

  function formatDateRange(openDateTime: string, closeDateTime: string) {
    if (!openDateTime || !closeDateTime) return '';

    // Use dayjs with UTC parsing for consistent timezone handling
    const openDate = dayjs.utc(openDateTime);
    const closeDate = dayjs.utc(closeDateTime);

    if (!openDate.isValid() || !closeDate.isValid()) return '';

    // Convert to Toronto timezone and format
    const openFormatted = openDate.tz('America/Toronto').format('MMM D');
    const closeFormatted = closeDate.tz('America/Toronto').format('MMM D');

    return `${openFormatted} - ${closeFormatted}`;
  }

  function getMonthName(month: string) {
    const months = [
      '',
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    return months[parseInt(month, 10)];
  }

  function formatDate(dateStr: string) {
    if (!dateStr) return '';

    // Use Eastern Time zone for consistent date display
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      timeZone: 'America/Toronto',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  function formatTime(dateStr: string) {
    if (!dateStr) return '';

    // Parse the UTC datetime string explicitly as UTC
    const utcDate = dayjs.utc(dateStr);
    if (!utcDate.isValid()) return dateStr; // Return original if invalid

    // Convert to Eastern Time using proper timezone conversion
    const easternOffset = new Date(dateStr).toLocaleString('en-US', {
      timeZone: 'America/Toronto',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${easternOffset.replace(':', '')} ET`;
  }

  // Helper function to format datetime for API requests
  function formatDateTimeForAPI(dateTimeStr: string): string {
    if (!dateTimeStr) return '';

    // If the string is already in the correct format, return as-is
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateTimeStr)) {
      return dateTimeStr;
    }

    // Parse the datetime string
    let date;

    // If it's an ISO string with timezone (from API), parse as UTC and convert to Eastern Time
    if (
      dateTimeStr.includes('T') &&
      (dateTimeStr.includes('Z') || dateTimeStr.includes('+'))
    ) {
      date = dayjs.utc(dateTimeStr).tz('America/Toronto');
    } else {
      // For other formats, parse normally
      date = dayjs(dateTimeStr);
    }

    if (!date.isValid()) return dateTimeStr;

    // Format to YYYY-MM-DD HH:mm:ss
    return date.format('YYYY-MM-DD HH:mm:ss');
  }

  const updateSimDate = useCallback(
    async (
      simDateId: number,
      updatedData: Partial<SimDateData>
    ): Promise<boolean> => {
      try {
        const requestBody: any = {
          simRequestID: simDateId,
          comment: updatedData.comment || ''
        };

        // Only include fields that are actually being updated
        if (updatedData.simYear !== undefined) {
          requestBody.simYear = updatedData.simYear;
        }
        if (updatedData.simMonth !== undefined) {
          requestBody.simMonth = updatedData.simMonth;
        }
        if (updatedData.simOpenDateTime !== undefined) {
          requestBody.simOpenDateTime = formatDateTimeForAPI(
            updatedData.simOpenDateTime
          );
        }
        if (updatedData.simCloseDateTime !== undefined) {
          requestBody.simCloseDateTime = formatDateTimeForAPI(
            updatedData.simCloseDateTime
          );
        }
        if (updatedData.simSendDate !== undefined) {
          requestBody.simSendDate = formatDateTimeForAPI(
            updatedData.simSendDate
          );
        }
        const requestObject = {
          body: requestBody
        };

        const response = await API.post(
          'crewlife',
          '/sim/settings/update',
          requestObject
        );

        if (response && response.simRequestID) {
          // If this is a comment-only update, don't update any date/time fields in local state
          if (updatedData.isCommentOnly) {
            // For comment-only updates, we don't need to update the local state
            // as the comment will be handled separately or the data will be refreshed
            return true;
          }

          const updatedSimDate: Partial<SimDateData> = {
            id: response.simRequestID
          };

          // Only update fields that were actually changed
          if (response.simYear !== undefined) {
            updatedSimDate.simYear = response.simYear;
          }
          if (response.simMonth !== undefined) {
            updatedSimDate.simMonth = getMonthName(response.simMonth);
          }
          if (
            response.simCloseDateTime !== undefined &&
            updatedData.simCloseDateTime !== undefined
          ) {
            updatedSimDate.simCloseDateTime = response.simCloseDateTime
              ? formatTime(response.simCloseDateTime)
              : '';
          }
          if (
            response.simSendDate !== undefined &&
            updatedData.simSendDate !== undefined
          ) {
            updatedSimDate.simSendDate = response.simSendDate
              ? formatDate(response.simSendDate)
              : '';
          }
          if (
            response.simOpenDateTime !== undefined &&
            updatedData.simOpenDateTime !== undefined
          ) {
            updatedSimDate.simOpenDateTime = response.simOpenDateTime;
            updatedSimDate.daterange = formatDateRange(
              response.simOpenDateTime,
              response.simCloseDateTime
            );
          }

          setSimDatesData((prevData) =>
            prevData.map((item) =>
              item.id === simDateId ? { ...item, ...updatedSimDate } : item
            )
          );
        }

        return true;
      } catch (e: any) {
        let errorMessage =
          'An unexpected error occurred while updating the SIM request.';

        if (e.response?.data?.details) {
          errorMessage = e.response.data.details;
        } else if (e.response?.data?.message) {
          errorMessage = e.response.data.message;
        } else if (e.response?.data?.error) {
          errorMessage = e.response.data.error;
        } else if (e.message) {
          errorMessage = e.message;
        }
        const customError = new Error(errorMessage);
        (customError as any).response = e.response;
        throw customError;
      }
    },
    []
  );
  const addComment = useCallback(
    async (
      simDateId: number,
      comment: Omit<CommentData, 'id'>
    ): Promise<boolean> => {
      try {
        // TODO: Replace with actual API call when backend is ready
        // const requestObject = {
        //     body: {
        //         simDateId,
        //         comment
        //     }
        // };
        // const response = await API.post('crewlife', '/sim-art-admin/comments', requestObject);

        // For now, update local state
        const newComment: CommentData = {
          ...comment,
          id: Date.now().toString() // Generate temporary ID
        };

        setSimDatesData((prevData) =>
          prevData.map((item) =>
            item.id === simDateId
              ? {
                  ...item,
                  comments: [...item.comments, newComment],
                  commentsData: item.commentsLength + 1
                }
              : item
          )
        );

        return true;
      } catch (e: any) {
        console.error('Error adding comment:', e);
        let errorMessage =
          'An unexpected error occurred while adding the comment.';

        if (e.response?.data?.details) {
          errorMessage = e.response.data.details;
        } else if (e.response?.data?.message) {
          errorMessage = e.response.data.message;
        } else if (e.response?.data?.error) {
          errorMessage = e.response.data.error;
        } else if (e.message) {
          errorMessage = e.message;
        }
        const customError = new Error(errorMessage);
        (customError as any).response = e.response;
        throw customError;
      }
    },
    []
  );

  const updateArtDate = useCallback(
    async (artDateId: number, updatedData: any): Promise<boolean> => {
      try {
        const requestBody: any = {
          artRequestID: artDateId,
          comment: updatedData.comment || ''
        };

        // Only include fields that are actually being updated
        if (updatedData.year !== undefined) {
          requestBody.artYear = updatedData.year;
        }
        if (updatedData.month !== undefined) {
          requestBody.artMonth = updatedData.month;
        }
        if (updatedData.artOpenDateTime !== undefined) {
          requestBody.artOpenDateTime = formatDateTimeForAPI(
            updatedData.artOpenDateTime
          );
        }
        if (updatedData.artCloseDateTime !== undefined) {
          requestBody.artCloseDateTime = formatDateTimeForAPI(
            updatedData.artCloseDateTime
          );
        }
        if (updatedData.artSendDate !== undefined) {
          requestBody.artSendDate = formatDateTimeForAPI(
            updatedData.artSendDate
          );
        }
        const requestObject = {
          body: requestBody
        };

        const response = await API.post(
          'crewlife',
          '/art/settings/update',
          requestObject
        );

        if (response && response.artRequestID) {
          // If this is a comment-only update, don't update any date/time fields in local state
          if (updatedData.isCommentOnly) {
            // For comment-only updates, we don't need to update the local state
            // as the comment will be handled separately or the data will be refreshed
            return true;
          }

          const updatedArtDate: Partial<ArtDateData> = {
            id: response.artRequestID
          };

          // Only update fields that were actually changed
          if (response.artYear !== undefined) {
            updatedArtDate.year = response.artYear;
          }
          if (response.artMonth !== undefined) {
            updatedArtDate.month = response.artMonth;
          }
          if (
            response.artCloseDateTime !== undefined &&
            updatedData.artCloseDateTime !== undefined
          ) {
            updatedArtDate.submissionClose = response.artCloseDateTime
              ? formatTime(response.artCloseDateTime)
              : '';
          }
          if (
            response.artSendDate !== undefined &&
            updatedData.artSendDate !== undefined
          ) {
            updatedArtDate.sendDates = response.artSendDate
              ? formatDate(response.artSendDate)
              : '';
          }
          if (
            response.artOpenDateTime !== undefined &&
            updatedData.artOpenDateTime !== undefined
          ) {
            updatedArtDate.artOpenDateTime = response.artOpenDateTime;
            updatedArtDate.daterange = formatDateRange(
              response.artOpenDateTime,
              response.artCloseDateTime
            );
          }

          setArtDatesData((prevData) =>
            prevData.map((item) =>
              item.id === artDateId ? { ...item, ...updatedArtDate } : item
            )
          );
        }

        return true;
      } catch (e: any) {
        let errorMessage =
          'An unexpected error occurred while updating the ART request.';

        if (e.response?.data?.details) {
          errorMessage = e.response.data.details;
        } else if (e.response?.data?.message) {
          errorMessage = e.response.data.message;
        } else if (e.response?.data?.error) {
          errorMessage = e.response.data.error;
        } else if (e.message) {
          errorMessage = e.message;
        }
        const customError = new Error(errorMessage);
        (customError as any).response = e.response;
        throw customError;
      }
    },
    []
  );

  return (
    <SimArtAdminContext.Provider
      value={{
        simDatesData,
        artDatesData,
        getSimDatesData,
        getArtDatesData,
        updateSimDate,
        updateArtDate,
        addComment
      }}
    >
      {props.children}
    </SimArtAdminContext.Provider>
  );
};

export const useSimArtAdminProvider = () => {
  return React.useContext(SimArtAdminContext);
};
